/**
 * yards.ts — What grows in the spaces between and behind the houses.
 *
 * The town is built as blocks of buildings ringed by footways, and everything
 * inside a block that isn't a house was, until this module existed, one
 * continuous sheet of aggregate. From above that reads as a car park with
 * cottages parked on it: the side passages between terraces, the strips behind
 * them and the undeveloped corners were all the same flat grey, so a block had
 * no interior at all.
 *
 * In a real market town that ground is **gardens and scrub** — grass gone to
 * seed, a hedge along a boundary, weeds banked up wherever a wall meets the
 * floor. This module works out where that goes.
 *
 * ## Why it's pure
 *
 * The same reason `terrace.ts` and `roofline.ts` are. Working out which ground
 * is free is a rectangle-subtraction problem with real edge cases — buildings
 * that touch, blocks with nothing in them, slivers too thin to plant — and
 * those are worth testing directly rather than eyeballing in a screenshot.
 * `render/world.ts` turns the answers into meshes and decides nothing.
 *
 * ## The one rule about what lands here
 *
 * **None of it is solid.** The game's existing rule is that trunks collide and
 * foliage does not, so you walk under branches; a bush and a clump of weeds are
 * all foliage, and they follow it. That isn't a shortcut — it's what lets the
 * planting go wherever it looks right, including the middle of a narrow gap,
 * without pinching a route, trapping the dog, or needing a clearance test of
 * its own. Wading through long grass is also exactly what "overgrown" should
 * look like.
 */
import { hash2 } from '../utils/math';
import { roadGround } from './pavement';
import type { Rect } from './level';
import type { RoadSegment } from './roads';

/** A place for one plant, with a size multiplier and a yaw to point it. */
export interface Plant {
  x: number;
  y: number;
  scale: number;
  /** Radians. Turns each one off the grid so a scatter never reads as rows. */
  yaw: number;
}

/** A circle to keep clear of — the bin, the home pad, a tree already standing. */
export interface KeepOut {
  x: number;
  y: number;
  r: number;
}

/**
 * The interiors of every block: the map, minus every piece of ground the road
 * network occupies, minus a margin at every block edge.
 *
 * That margin is what keeps the town looking like a town. Run the rough ground
 * right up to the kerb and every street is fringed with weeds, which reads as
 * abandoned rather than lived-in; leaving a strip of the original hardstanding
 * along each frontage gives the cottages a paved apron to stand on and puts the
 * scruff *behind* them, where it belongs. It also does the right thing to a
 * side passage on its own: the mouth at the street stays paved and the far end
 * goes to seed.
 *
 * ## Why this is a subtraction and not a grid
 *
 * It used to cut the map's two axes independently and take the cartesian
 * product of the results, which is a great deal cheaper and is **a grid by
 * construction**. Every road ran the full width of the map, so it had to be:
 * with no road able to stop, no block could ever be longer than the one beside
 * it, and a town of nine blocks was always three by three.
 *
 * Subtracting the road ground from the whole map instead makes a block whatever
 * shape the streets leave behind. A road that stops partway no longer separates
 * anything past its end, so the two blocks it would have divided arrive as one
 * long one — which is the entire mechanism behind uneven blocks, and it falls
 * out of `freeRects` rather than needing a rule of its own.
 *
 * `roadGround` is asked for the blockers rather than re-deriving the corridors
 * here, so the ground the gardens are laid on cannot drift from the paving that
 * is actually drawn.
 */
export function blockInteriors(
  world: { width: number; height: number },
  roads: readonly RoadSegment[],
  roadWidth: number,
  pavementWidth: number,
  margin: number,
): Rect[] {
  const whole: Rect = { x: 0, y: 0, w: world.width, h: world.height };
  const blockers = roadGround(world, roads, roadWidth, pavementWidth);

  return mergeRects(freeRects(whole, blockers))
    .map((r) => ({
      x: r.x + margin,
      y: r.y + margin,
      w: r.w - margin * 2,
      h: r.h - margin * 2,
    }))
    .filter((r) => r.w > 0 && r.h > 0);
}

/**
 * The ground inside `block` that no blocker covers, as a list of rectangles.
 *
 * A vertical sweep: cut the block at every blocker edge, and in each resulting
 * slab subtract the blockers' y-spans from the block's own. Slabs whose
 * leftovers match are then merged back together, which matters — the ground is
 * textured, and a run of one-unit-wide strips would tile the pattern
 * differently in each one and show every seam.
 *
 * Anything thinner than `min` in either direction is dropped. Those slivers are
 * the gap between two buildings that were meant to touch, and planting a hedge
 * in one puts a bush inside a wall.
 */
export function freeRects(block: Rect, blockers: readonly Rect[], min = 0): Rect[] {
  const x0 = block.x;
  const x1 = block.x + block.w;
  const y0 = block.y;
  const y1 = block.y + block.h;

  const clipped = blockers
    .map((b) => ({
      x0: Math.max(b.x, x0),
      x1: Math.min(b.x + b.w, x1),
      y0: Math.max(b.y, y0),
      y1: Math.min(b.y + b.h, y1),
    }))
    .filter((b) => b.x1 > b.x0 && b.y1 > b.y0);

  const cuts = [...new Set([x0, x1, ...clipped.flatMap((b) => [b.x0, b.x1])])]
    .filter((x) => x >= x0 && x <= x1)
    .sort((a, b) => a - b);

  // One column of leftover y-spans per slab, so identical neighbours can merge.
  const columns: { from: number; to: number; spans: [number, number][] }[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const from = cuts[i];
    const to = cuts[i + 1];
    if (to - from <= 0) continue;
    const mid = (from + to) / 2;
    const covering = clipped
      .filter((b) => b.x0 <= mid && b.x1 >= mid)
      .map((b): [number, number] => [b.y0, b.y1]);
    columns.push({ from, to, spans: subtractSpans(y0, y1, covering) });
  }

  const out: Rect[] = [];
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    let to = col.to;
    // Absorb any following slab with exactly the same leftovers.
    while (i + 1 < columns.length && sameSpans(columns[i + 1].spans, col.spans)) {
      to = columns[++i].to;
    }
    for (const [a, b] of col.spans) {
      if (to - col.from >= min && b - a >= min) {
        out.push({ x: col.from, y: a, w: to - col.from, h: b - a });
      }
    }
  }
  return out;
}

/**
 * Glue rectangles back together wherever two of them share a whole edge.
 *
 * `freeRects` sweeps vertically, so it describes an L-shaped piece of ground as
 * a stack of narrow columns. That is the right answer for planting — every
 * column really is free — and the wrong one for **blocks**: the ground below a
 * cul-de-sac is one wide block a terrace can run across, and handing it over as
 * five 48-unit strips means four of them are too thin to build on and the fifth
 * is a sliver. The town then grows houses only at the map edges.
 *
 * So adjacent pieces with matching extents are merged back, to a fixpoint —
 * a pass can expose a new pair, as three columns of equal height only become
 * one rect after the first two have joined.
 *
 * Only whole-edge matches merge. Partial overlaps would need a real rectangle
 * union, which produces overlapping output, and every consumer here assumes
 * blocks are disjoint.
 */
export function mergeRects(rects: readonly Rect[]): Rect[] {
  const out = rects.map((r) => ({ ...r }));

  for (let joined = true; joined; ) {
    joined = false;
    outer: for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i];
        const b = out[j];

        const sideBySide = a.y === b.y && a.h === b.h && (a.x + a.w === b.x || b.x + b.w === a.x);
        const stacked = a.x === b.x && a.w === b.w && (a.y + a.h === b.y || b.y + b.h === a.y);
        if (!sideBySide && !stacked) continue;

        out[i] = {
          x: Math.min(a.x, b.x),
          y: Math.min(a.y, b.y),
          w: sideBySide ? a.w + b.w : a.w,
          h: stacked ? a.h + b.h : a.h,
        };
        out.splice(j, 1);
        joined = true;
        break outer;
      }
    }
  }
  return out;
}

/** What's left of [from, to) once every span in `covers` is removed. */
function subtractSpans(from: number, to: number, covers: [number, number][]): [number, number][] {
  const sorted = [...covers].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  let at = from;
  for (const [a, b] of sorted) {
    if (a > at) out.push([at, Math.min(a, to)]);
    at = Math.max(at, b);
    if (at >= to) break;
  }
  if (at < to) out.push([at, to]);
  return out.filter(([a, b]) => b > a);
}

function sameSpans(a: [number, number][], b: [number, number][]): boolean {
  return a.length === b.length && a.every((s, i) => s[0] === b[i][0] && s[1] === b[i][1]);
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * Scatter plants across `rect` on a jittered grid.
 *
 * A grid rather than pure random placement, because random scatter clumps: at
 * the densities here you reliably get three bushes in a heap and a bare patch
 * beside them. Jittering each cell by most of its own width keeps the spacing
 * honest while destroying the rows — and being derived from world position (see
 * `hash2`) means the same garden grows the same way on every run, which is what
 * makes a screenshot comparison mean anything.
 *
 * `inset` holds plants off the rect's own edges, which are building walls: a
 * bush centred on the boundary is half inside a cottage.
 */
export function scatter(
  rect: Rect,
  spacing: number,
  inset: number,
  keepOut: readonly KeepOut[] = [],
  jitter = 0.7,
): Plant[] {
  const x0 = rect.x + inset;
  const y0 = rect.y + inset;
  const w = rect.w - inset * 2;
  const h = rect.h - inset * 2;
  if (w <= 0 || h <= 0 || spacing <= 0) return [];

  const cols = Math.max(1, Math.round(w / spacing));
  const rows = Math.max(1, Math.round(h / spacing));
  const cw = w / cols;
  const ch = h / rows;

  const out: Plant[] = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const cx = x0 + (i + 0.5) * cw;
      const cy = y0 + (j + 0.5) * ch;
      const x = cx + (hash2(cx, cy) - 0.5) * cw * jitter;
      const y = cy + (hash2(cy, cx + 37) - 0.5) * ch * jitter;
      if (x < x0 || x > x0 + w || y < y0 || y > y0 + h) continue;
      if (keepOut.some((k) => Math.hypot(x - k.x, y - k.y) < k.r)) continue;
      out.push({
        x,
        y,
        scale: 0.7 + hash2(x + 11, y - 7) * 0.6,
        yaw: hash2(x - 3, y + 19) * Math.PI * 2,
      });
    }
  }
  return out;
}

/**
 * Plants banked along a rectangle's perimeter — `outward` to grow *against* it
 * from outside (the base of a wall, the edge of a paved patio), or inward to
 * fringe the inside of a boundary.
 *
 * This is the half that sells "overgrown". An even scatter over open ground
 * reads as planting — deliberate, tended. What reads as neglect is growth
 * banked up where something meets the floor while the middle stays worn, which
 * is also where it really grows.
 *
 * **Run this against walls, not against free ground.** The first version walked
 * the perimeter of each free-ground rectangle instead, which sounds equivalent
 * and isn't: those rectangles come out of `freeRects`' vertical sweep, so most
 * of their edges are seams in the decomposition rather than anything you can
 * see. The result was tidy lines of weeds ruled across the middle of an open
 * lawn, growing along nothing at all.
 */
export function perimeterTufts(
  rect: Rect,
  spacing: number,
  band: number,
  outward: boolean,
  keepOut: readonly KeepOut[] = [],
): Plant[] {
  if (spacing <= 0 || band <= 0) return [];
  if (!outward && (rect.w <= band * 2 || rect.h <= band * 2)) return [];
  const dir = outward ? -1 : 1; // which way "into the band" points
  const out: Plant[] = [];

  /**
   * Walk one edge from (ax, ay) to (bx, by), placing tufts along it and pushing
   * each one off it by a random slice of `band` along the (nx, ny) normal.
   *
   * The along-jitter is clamped to the edge's own span. Without that, a tuft
   * near a corner slides past the end of the wall it's supposed to be growing
   * against — harmless outside a building, but on an inward fringe it puts the
   * weed over the boundary and onto the paving.
   */
  const edge = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
    nx: number,
    ny: number,
  ): void => {
    const vertical = by !== ay;
    const length = Math.hypot(bx - ax, by - ay);
    const n = Math.max(1, Math.round(length / spacing));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const ex = ax + (bx - ax) * t;
      const ey = ay + (by - ay) * t;
      const along = (hash2(ex, ey) - 0.5) * spacing * 0.6;
      const into = hash2(ey + 5, ex - 5) * band;
      const x = (vertical ? ex : clamp(ex + along, ax, bx)) + nx * into;
      const y = (vertical ? clamp(ey + along, ay, by) : ey) + ny * into;
      if (keepOut.some((k) => Math.hypot(x - k.x, y - k.y) < k.r)) continue;
      out.push({
        x,
        y,
        scale: 0.65 + hash2(x - 13, y + 3) * 0.7,
        yaw: hash2(x + 29, y - 11) * Math.PI * 2,
      });
    }
  };

  const { x, y, w, h } = rect;
  edge(x, y, x + w, y, 0, dir); // top
  edge(x, y + h, x + w, y + h, 0, -dir); // bottom
  edge(x, y, x, y + h, dir, 0); // left
  edge(x + w, y, x + w, y + h, -dir, 0); // right
  return out;
}

/** Total ground area of a set of rects — used by the tests to pin the sweep. */
export function areaOf(rects: readonly Rect[]): number {
  return rects.reduce((a, r) => a + r.w * r.h, 0);
}
